import MetricBox from "components-for/display/metric-box";
import { calculateValue } from "../agent-metrics.utilities";
import { FunctionComponent, memo } from "react";
import { IProps } from "./agent-sentiment-metric.types";
import { parseUrlArray } from "@edgetier/utilities";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import { useInteractionsCount } from "hooks-for/interactions/use-interactions-counts";
import { useQueryParameters } from "@edgetier/client-components";
import "./agent-sentiment-metric.scss";

/**
 * Show what percentage of an agent's interactions contain a certain sentiment versus all agents overall.
 * @param props.filterName Name of the query parameter to use.
 * @param props.icon       Sentiment icon.
 * @param props.title      Name of the sentiment.
 * @returns                Sentiment metric.
 */
const AgentSentimentMetric: FunctionComponent<IProps> = ({
    defaultEndDateTime,
    defaultStartDateTime,
    filterName,
    icon,
    title,
    tooltipContent,
}) => {
    const filters = useInteractionQueryParameters({
        defaults: {
            startDateTime: defaultStartDateTime,
            endDateTime: defaultEndDateTime,
        },
    });

    const { comparisonAgentId, reviewAgentId } = useQueryParameters({
        comparisonAgentId: parseUrlArray,
        reviewAgentId: String,
    });

    // Count the interactions handled by this agent.
    const agentParameters = { ...filters, agentId: reviewAgentId };
    const { data: agentCount, isLoading: isAgentInteractionsCountLoading } = useInteractionsCount(agentParameters);

    // Count the interactions handled by everyone.
    const comparisonParameters = { ...filters, agentId: comparisonAgentId };
    const { data: overallCount, isLoading: isOverallInteractionsCountLoading } =
        useInteractionsCount(comparisonParameters);

    // Count the interactions handled by this agent with the given sentiment.
    const agentSentimentParameters = { ...filters, agentId: reviewAgentId, [filterName]: true };
    const { data: agentSentimentCount, isLoading: isAgentDataLoading } = useInteractionsCount(agentSentimentParameters);

    // Count the interactions handled by everyone with the given sentiment.
    const comparisonSentimentParameters = { ...filters, agentId: comparisonAgentId, [filterName]: true };
    const { data: overallSentimentCount, isLoading: isOverallDataLoading } =
        useInteractionsCount(comparisonSentimentParameters);

    return (
        <MetricBox
            comparisonValue={calculateValue(overallCount, overallSentimentCount)}
            icon={icon}
            isLoading={
                isAgentInteractionsCountLoading ||
                isOverallInteractionsCountLoading ||
                isAgentDataLoading ||
                isOverallDataLoading
            }
            options={{ maximumFractionDigits: 0 }}
            title={title}
            tooltipContent={tooltipContent}
            unit="%"
            value={calculateValue(agentCount, agentSentimentCount)}
        />
    );
};

export default memo(AgentSentimentMetric);
